import { useRef } from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';

const EASE = [0.22, 1, 0.36, 1];

// Four corner bracket marks around the product frame
const CORNERS = [
  'top-0 left-0 border-t border-l',
  'top-0 right-0 border-t border-r',
  'bottom-0 left-0 border-b border-l',
  'bottom-0 right-0 border-b border-r',
];

export default function HeroStage({ image, name, specs = [] }) {
  const ref = useRef(null);
  const rawX = useMotionValue(0.5);
  const rawY = useMotionValue(0.5);

  const rotateY = useSpring(useTransform(rawX, [0, 1], [-8, 8]), { stiffness: 50, damping: 16 });
  const rotateX = useSpring(useTransform(rawY, [0, 1], [6, -6]), { stiffness: 50, damping: 16 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    rawX.set((e.clientX - r.left) / r.width);
    rawY.set((e.clientY - r.top) / r.height);
  };

  const onLeave = () => {
    rawX.set(0.5);
    rawY.set(0.5);
  };

  return (
    <div
      ref={ref}
      className="relative w-full flex items-center justify-center select-none"
      style={{ minHeight: '520px', perspective: '960px' }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid="hero-stage"
    >
      {/* Ambient particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        {[...Array(9)].map((_, i) => (
          <div
            key={i}
            className="hero-particle"
            style={{
              left: `${8 + i * 10}%`,
              top: `${12 + (i % 4) * 20}%`,
              animationDelay: `${i * 0.9}s`,
              animationDuration: `${5 + (i % 3) * 2}s`,
              opacity: 0.3 + (i % 3) * 0.15,
              width: i % 3 === 0 ? '3px' : '2px',
              height: i % 3 === 0 ? '3px' : '2px',
            }}
          />
        ))}
      </div>

      {/* Outer orbit ring (slow) */}
      <div
        className="absolute pointer-events-none"
        style={{
          left: '50%', top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '520px', height: '520px',
        }}
      >
        <div style={{ width: '100%', height: '100%', transform: 'scaleY(0.3)' }}>
          <div className="orbit-ring-outer" />
        </div>
      </div>

      {/* Inner orbit ring (faster, reverse) */}
      <div
        className="absolute pointer-events-none"
        style={{
          left: '50%', top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '380px', height: '380px',
        }}
      >
        <div style={{ width: '100%', height: '100%', transform: 'scaleY(0.28)' }}>
          <div className="orbit-ring-inner" />
        </div>
      </div>

      {/* Product card with 3D tilt */}
      <motion.div
        className="relative z-10"
        style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
      >
        <motion.div
          className="relative"
          animate={{ scale: [1, 1.015, 1] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        >
          {/* Image frame */}
          <div
            className="relative overflow-hidden bg-mb-surface"
            style={{
              width: 'clamp(230px, 28vw, 320px)',
              aspectRatio: '3/4',
              border: '0.5px solid rgba(210, 177, 96, 0.28)',
            }}
          >
            <img
              src={image}
              alt={name || 'MR. BOSS & CO.'}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: 'brightness(0.85) contrast(1.1) saturate(0.9)' }}
              onError={(e) => { e.currentTarget.style.opacity = '0'; }}
            />
            {/* Radial depth vignette */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'radial-gradient(ellipse at 55% 38%, transparent 28%, rgba(8,9,10,0.62) 100%)',
              }}
            />
            {/* Grain noise overlay */}
            <div
              className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-20"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                backgroundSize: '180px 180px',
              }}
            />
            {/* Bottom info bar */}
            <motion.div
              className="absolute bottom-0 left-0 right-0 px-4 py-3"
              style={{
                background: 'linear-gradient(to top, rgba(8,9,10,0.92) 0%, transparent 100%)',
              }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
            >
              <div
                className="eyebrow-sm text-mb-muted mb-0.5"
                style={{ fontSize: '8px', letterSpacing: '0.14em' }}
              >
                HOUSE SIGNATURE
              </div>
              <div className="font-display text-[11px] text-mb leading-snug">{name || 'The Legacy'}</div>
            </motion.div>
          </div>

          {/* Corner brackets */}
          {CORNERS.map((cls, i) => (
            <motion.div
              key={i}
              className={`absolute w-3.5 h-3.5 ${cls}`}
              style={{ borderColor: 'rgba(210,177,96,0.65)' }}
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.55 + i * 0.07, duration: 0.35, ease: EASE }}
            />
          ))}

          {/* Side ruler marks */}
          <motion.div
            className="absolute top-1/2 -left-6 flex flex-col items-end gap-[3px]"
            style={{ transform: 'translateY(-50%)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
          >
            {[...Array(7)].map((_, i) => (
              <div
                key={i}
                style={{
                  width: i % 3 === 0 ? '12px' : '6px',
                  height: '0.5px',
                  background: 'rgba(210,177,96,0.35)',
                }}
              />
            ))}
          </motion.div>
          <motion.div
            className="absolute top-1/2 -right-6 flex flex-col items-start gap-[3px]"
            style={{ transform: 'translateY(-50%)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.8 }}
          >
            {[...Array(7)].map((_, i) => (
              <div
                key={i}
                style={{
                  width: i % 3 === 0 ? '12px' : '6px',
                  height: '0.5px',
                  background: 'rgba(210,177,96,0.35)',
                }}
              />
            ))}
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Floating spec annotations — large screens only */}
      {specs.slice(0, 4).map((spec, i) => {
        const pos = [
          { top: '22%', left: '3%' },
          { top: '32%', right: '3%' },
          { bottom: '28%', left: '3%' },
          { bottom: '20%', right: '3%' },
        ][i];
        const isLeft = i % 2 === 0;
        return (
          <motion.div
            key={spec}
            className="absolute hidden xl:flex items-center gap-2 pointer-events-none whitespace-nowrap"
            style={{ fontSize: '9px', letterSpacing: '0.1em', color: 'var(--mb-text-muted)', ...pos }}
            initial={{ opacity: 0, x: isLeft ? -14 : 14 }}
            animate={{ opacity: 0.75, x: 0 }}
            transition={{ delay: 1.8 + i * 0.16, duration: 0.7, ease: EASE }}
          >
            {isLeft ? (
              <>
                <span
                  className="inline-block flex-shrink-0"
                  style={{ width: '20px', height: '0.5px', background: 'rgba(210,177,96,0.45)' }}
                />
                <span className="font-sans uppercase tracking-widest">{spec}</span>
              </>
            ) : (
              <>
                <span className="font-sans uppercase tracking-widest">{spec}</span>
                <span
                  className="inline-block flex-shrink-0"
                  style={{ width: '20px', height: '0.5px', background: 'rgba(210,177,96,0.45)' }}
                />
              </>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}
